import type { CampaignDownloadAvailability, CampaignFile } from '@/domain/campaign.types'
import { isDistributionPackageFile } from '@/lib/campaign-download-capabilities'

export type CampaignDownloadMenuProps =
	| {
			context: 'individual'
			file: CampaignFile
			availability: CampaignDownloadAvailability
			onDownload: (fileId: string) => void
	  }
	| {
			context: 'aggregate'
			selectionCount: number
			selectedAvailability: CampaignDownloadAvailability
			allAvailability: CampaignDownloadAvailability
			/**
			 * Optional only so every pre-existing aggregate-context caller (stories,
			 * tests) keeps compiling before it is updated to supply them — the real
			 * Campaign composition (`CampaignFileGallery` via `CampaignDetail`)
			 * always provides all four.
			 */
			onDownloadSelected?: () => void
			onDownloadAll?: () => void
			isPending?: boolean
			errorMessage?: string
	  }

/**
 * The only component type rendering a download control. A per-File instance
 * (`context: "individual"`) renders that File's individual action — a
 * `distribution_package` File uses this same instance, never a second package
 * control. The gallery-level instance (`context: "aggregate"`) renders only
 * selected-download and download-all, disabled with an explanation, unaffected
 * by selection count. This component only reports the click; obtaining a URL and
 * handing it to the browser belongs to the download port behind the container.
 */
export function CampaignDownloadMenu(props: CampaignDownloadMenuProps) {
	if (props.context === 'individual') {
		const { file, availability, onDownload } = props
		const isAvailable = availability === 'available'
		const label = isDistributionPackageFile(file) ? 'Download package' : 'Download'
		return (
			<button
				type="button"
				onClick={() => onDownload(file.fileId)}
				disabled={!isAvailable}
				aria-disabled={!isAvailable}
				// The visible copy stays short because the surrounding presentation already
				// shows the filename, but a gallery renders one of these per File — so the
				// announced name has to say which File it downloads. Both labels are built
				// from the one expression above, so the card and the package row cannot
				// diverge. `title` still carries the unavailable explanation as this
				// control's accessible description.
				aria-label={`${label} ${file.fileName}`}
				title={isAvailable ? undefined : 'Download is not available for this File.'}
				className="rounded-ds-md border border-ds-border px-3 py-1.5 text-sm text-ds-text hover:bg-ds-surface disabled:opacity-50"
			>
				{label}
			</button>
		)
	}

	const {
		selectionCount,
		selectedAvailability,
		allAvailability,
		onDownloadSelected,
		onDownloadAll,
		isPending,
		errorMessage,
	} = props
	const selectedCapable = selectedAvailability === 'available'
	const hasSelection = selectionCount > 0
	const allCapable = allAvailability === 'available'
	const pending = isPending ?? false

	const selectedTitle = pending
		? 'Preparing your download…'
		: !selectedCapable
			? 'Selected-download is not supported yet.'
			: !hasSelection
				? 'Select at least one File to download.'
				: undefined
	const allTitle = pending ? 'Preparing your download…' : !allCapable ? 'Download-all is not supported yet.' : undefined

	return (
		<div className="flex flex-col gap-1">
			<div className="flex gap-2">
				<button
					type="button"
					onClick={() => onDownloadSelected?.()}
					disabled={!selectedCapable || !hasSelection || pending}
					aria-disabled={!selectedCapable || !hasSelection || pending}
					title={selectedTitle}
					className="rounded-ds-md border border-ds-border px-3 py-1.5 text-sm text-ds-text disabled:opacity-50"
				>
					Download selected ({selectionCount})
				</button>
				<button
					type="button"
					onClick={() => onDownloadAll?.()}
					disabled={!allCapable || pending}
					aria-disabled={!allCapable || pending}
					title={allTitle}
					className="rounded-ds-md border border-ds-border px-3 py-1.5 text-sm text-ds-text disabled:opacity-50"
				>
					Download all assets
				</button>
			</div>
			{errorMessage && (
				<p role="alert" className="text-xs text-ds-danger">
					{errorMessage}
				</p>
			)}
		</div>
	)
}
